import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  createClinicScheduleController,
  CreateClinicScheduleModel,
  createWeeklyScheduleController,
  deleteClinicScheduleController,
  DeleteClinicScheduleModel,
  getClinicScheduleController,
  GetClinicScheduleModel, 
  getPublicWeeklyScheduleController,
  GetResponseClinicScheduleModel,
  getWeeklyScheduleController,
  MutateResponseClinicScheduleModel,
  QueryKeyClinicSchedule,
  updateClinicScheduleController,
  UpdateClinicScheduleModel,
} from "@factory/model/query-model-clinic-schedule";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

export function useQyGetClinicSchedule(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseClinicScheduleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getClinicScheduleController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      undefined,
      undefined,
      undefined,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as GetResponseClinicScheduleModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyClinicSchedule, search, limit, offset, order, filter],
    queryFn: () => apiFn(search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetWeeklySchedule(
  tenant: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetClinicScheduleModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await getWeeklyScheduleController(tenant, authHeaders());
    const response = (await operation()).data;
    return response.data as GetClinicScheduleModel[];
  };

  return useQuery({
    enabled: enabled && !!tenant,
    queryKey: [QueryKeyClinicSchedule, "weekly", tenant],
    queryFn: () => apiFn(tenant),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetPublicWeeklySchedule(
  tenant: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetClinicScheduleModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string) => {
    showProgress();
    const operation = await getPublicWeeklyScheduleController(tenant);
    const response = (await operation()).data;
    return response.data as GetClinicScheduleModel[];
  };

  return useQuery({
    enabled: enabled !== false && !!tenant,
    queryKey: [QueryKeyClinicSchedule, "public-weekly", tenant],
    queryFn: () => apiFn(tenant),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateClinicSchedule(
  onSuccess?:
    | ((data: MutateResponseClinicScheduleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreateClinicScheduleModel) => {
    await checkSession();

    showProgress();
    const operation = await createClinicScheduleController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseClinicScheduleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyClinicSchedule);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateWeeklySchedule(
  onSuccess?:
    | ((data: MutateResponseClinicScheduleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError, showSuccess } =
    useNotificationContext();

  const apiFn = async (payload: {
    tenant: string;
    schedules: CreateClinicScheduleModel[];
  }) => {
    await checkSession();

    showProgress();
    const operation = await createWeeklyScheduleController(
      payload.tenant,
      payload.schedules,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseClinicScheduleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyClinicSchedule);
      showSuccess("Weekly schedule saved successfully");
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateClinicSchedule(
  onSuccess?:
    | ((data: MutateResponseClinicScheduleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdateClinicScheduleModel) => {
    await checkSession();

    showProgress();
    const operation = await updateClinicScheduleController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseClinicScheduleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyClinicSchedule);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteClinicSchedule(
  onSuccess?:
    | ((data: MutateResponseClinicScheduleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteClinicScheduleModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteClinicScheduleController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseClinicScheduleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyClinicSchedule);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
